<script setup>
/**
 * ------------------------------------------------
 * Central UI component that renders the expandable product filter sidebar.
 *
 * • Translates heterogenous filter metadata (BOOLEAN, PRICE, NUMBER, OPTION,
 *   TPG_OPTION) coming from the backend into accessible Vue controls
 *   (switches, sliders, checkboxes, number spinners).
 * • Keeps a *single* source of truth – `filterStore.selected` – so every
 *   filter change is reflected across the application
 * • Applies a debounce before calling `filterStore.applyFilters()` to
 *   avoid hammering the API while the user is still interacting, which greatly
 *   improves perceived performance.
 * • Houses only presentation/UX glue; domain logic lives inside Pinia stores,
 *   keeping concerns cleanly separated.
 */
import {onMounted, onBeforeUnmount, computed, reactive, watch, nextTick, ref} from 'vue'
import {useFilterStore} from '@/store/filter'
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'
import {getUnitOrCurrency} from '@/utils/format'
import NumberSpinner from '@/components/NumberSpinner.vue'
import {useProductListStore} from "@/store";
import {getBreakpoint} from '@/utils/breakpoint'
import {
  getDecimalLocale,
  formatLocalizedDecimal,
  parseLocalizedDecimal,
} from '@/utils/format'

const decimalLocale = getDecimalLocale()
const filterStore = useFilterStore()
const productStore = useProductListStore()
const SHOW_MORE_OPTIONS_THRESHOLD = 10

const expanded = reactive({})
let debounceTimer
const LAST_OPEN_STORAGE_KEY = 'filters:lastOpenKey'
const lastOpenKey = ref(sessionStorage.getItem(LAST_OPEN_STORAGE_KEY) || null)

function setLastOpenKey(key) {
  lastOpenKey.value = key
  try {
    sessionStorage.setItem(LAST_OPEN_STORAGE_KEY, key)
    // eslint-disable-next-line no-empty
  } catch {
  }
}

function openOnly(key, opts = {fetch: true}) {
  if (!key) return

  // close all, only expand key
  Object.keys(expanded).forEach(k => {
    expanded[k] = (k === key)
  })

  // set key true if not already
  if (expanded[key] !== true) expanded[key] = true

  // only fetch if allowed e.g. when user click on header
  if (opts.fetch && !filterStore.loadingOptions?.[key]) {
    filterStore.fetchOptions(key)
  }
}

// Map bp index -> debounce ms
function debounceMsForBp(bpIndex) {
  // e.g. 0=mobile, 1=tablet, 2=desktop, 3=wide
  const table = [5000, 5000, 2500, 2050]
  return table[bpIndex] ?? 2500
}

/**
 * Reactive map whose keys are category IDs and whose values are booleans
 * indicating whether that category is currently in an *active* (non‑default)
 * state.
 *
 * The computed value recalculates whenever `filterStore.selected` changes and
 * powers both the visibility of each category’s “reset” button and the auto‑
 * expansion of accordion panels that already contain an active filter.
 */
const activeByKey = computed(() => {
  /** @type {{ [k: string]: boolean }} */
  const map = {}

  categoriesWithMeta.value.forEach(({key, meta}) => {
    const sel = filterStore.selected[key]
    if (!meta) {
      map[key] = false;
      return
    }

    let active = false
    switch (meta.type) {
      case 'BOOLEAN':
        active = sel === true
        break
      case 'PRICE':
      case 'NUMBER':
        active =
          Array.isArray(sel) &&
          sel.length === 2 &&
          (sel[0] !== meta.min || sel[1] !== meta.max)
        break
      case 'TPG_OPTION':
      case 'OPTION':
        active = Array.isArray(sel) && sel.length > 0
        break
    }

    map[key] = active
  })

  return map
})

/**
 * Toggles a filter category panel.
 * Opens the selected panel (closing others), sets it as the last interacted key,
 * and fetches its options if needed. Closes it if already open.
 */
function toggleExpand(key) {
  const willOpen = !expanded[key]
  if (willOpen) {
    filterStore.setLastInteractedKey(key)
    openOnly(key)
    setLastOpenKey(key)
  } else {
    expanded[key] = false
  }
}

/**
 * Normalise an option coming from the backend into the primitive value that
 * should be stored inside `filterStore.selected[categoryKey]`.
 * The API is not consistent, hence the rather defensive fallbacks.
 */
function valueFor(categoryKey, opt) {
  // productCategory: send the option.key (TPG key)
  if (categoryKey === 'productCategory') {
    if (opt && typeof opt === 'object') return opt.key ?? opt.tpgCode ?? opt.value ?? opt.code ?? opt.id
    return opt
  }

  // TPG_OPTION prefers tpgCode
  const meta = filterStore.metaFor(categoryKey)
  if (meta?.type === 'TPG_OPTION') {
    if (opt && typeof opt === 'object') return opt.tpgCode ?? opt.value ?? opt.code ?? opt.key ?? opt.id
    return opt
  }

  // Generic OPTION
  if (opt && typeof opt === 'object') {
    return (
      opt.value ??
      opt.code ??
      opt.key ??
      opt.id ??
      opt.tpgCode ??
      opt.label ??
      opt.name ??
      opt.tpgName
    )
  }
  return opt
}

/**
 * Returns the human‑readable label for an option. Falls back to `String(opt)`
 * if a more user‑friendly field is not present.
 */
function labelFor(opt) {
  return (opt && typeof opt === 'object')
    ? (opt.tpgName ?? opt.label ?? opt.name ?? opt.tpgCode ?? opt.key ?? opt.value ?? opt.code ?? String(opt))
    : String(opt)
}

const availableOptionsByKey = computed(() => {
  const map = {}
  for (const cat of categoriesWithMeta.value) {
    const eff = filterStore.effectiveMetaFor(cat.key)
    map[cat.key] = eff?.optionValues || []
  }
  return map
})

/**
 * Convenience wrapper to test whether a checkbox in a given category is
 * currently selected.
 */
function isChecked(categoryKey, value) {
  return (filterStore.selected[categoryKey] || []).includes(value)
}

let initialized = false;

onMounted(async () => {
  await nextTick();
  if (initialized) return;

  // ensure expanded map exists for all keys
  const keys = categoriesWithMeta.value.map(c => c.key);
  keys.forEach(k => {
    if (expanded[k] === undefined) expanded[k] = false;
  });

  // choose which key to open (session wins, never the URL)
  const preferred = filterStore.lastInteractedKey;
  const fallback = keys.includes('productCategory') ? 'productCategory' : keys[0];
  const keyToOpen = preferred || fallback;

  // open it and fetch options (exactly one fetch on first paint)
  openOnly(keyToOpen, {fetch: true});
  setLastOpenKey(keyToOpen);

  initialized = true;
});

function decimalPlaces(n) {
  const s = String(n)
  const i = s.indexOf('.')
  return i === -1 ? 0 : (s.length - i - 1)
}

// Normalize numeric meta:
// - unify decimals for min/max
// - __interval = base precision (10^-dec) → satisfies slider rule
// - __step     = max(1, 10% of max), rounded to the same decimals
function normalizeNumericMeta(raw) {
  let min = Number(raw.min)
  let max = Number(raw.max)

  const dec = Math.max(decimalPlaces(min), decimalPlaces(max))
  const round = (v) => Number(v.toFixed(dec))

  min = round(min)
  max = round(max)

  const baseInterval = dec > 0 ? Number((1 / (10 ** dec)).toFixed(dec)) : 1
  const step = round(Math.max(1, max * 0.1))

  return {...raw, min, max, __decimals: dec, __interval: baseInterval, __step: step}
}

/**
 * Memoised list of categories enriched with min/max rounding and with
 * degenerate ranges filtered out (where min === max).
 */
const categoriesWithMeta = computed(() =>
  filterStore.filterCategories
    .map(cat => {
      const raw = filterStore.metaFor(cat.key)

      // If meta not loaded yet, keep the category (meta: null) so headers render and we can lazy-load on open.
      if (!raw) return {key: cat.key, meta: null}

      // clone and round values to avoid floating errors
      let meta = {...raw}

      if (meta.type === 'PRICE' || meta.type === 'NUMBER') {
        meta = normalizeNumericMeta(meta)
      }
      return {key: cat.key, meta}
    })
    .filter(Boolean)
)

function snapSelectionToGrid(key, val, idx) {
  const cat = categoriesWithMeta.value.find(c => c.key === key)
  const meta = cat?.meta
  if (!meta) return

  const [aOld, bOld] = filterStore.selected[key] || [meta.min, meta.max]
  const [a, b] = Array.isArray(val) ? val : [aOld, bOld]

  const s = meta.__step || 1
  const dec = meta.__decimals || 0
  const snap = (v) => Number(Math.round((v - meta.min) / s) * s + meta.min).toFixed(dec) * 1

  let na = aOld, nb = bOld

  if (typeof idx === 'number') {
    if (idx === 0) {
      na = snap(a)
    } // snap only MIN
    if (idx === 1) {
      nb = snap(b)
    } // snap only MAX
  } else {
    // No index (keyboard/change): snap only the value that actually changed
    if (a !== aOld) na = snap(a)
    if (b !== bOld) nb = snap(b)
  }

  // keep bounds ordered
  if (na > nb) {
    if (typeof idx === 'number' && idx === 0) na = Math.min(na, nb)
    else nb = Math.max(nb, na)
  }

  if (na !== aOld) filterStore.updateRange(key, 0, na)
  if (nb !== bOld) filterStore.updateRange(key, 1, nb)

  localRanges[key] = {
    min: formatLocalizedDecimal(na, {locale: decimalLocale, decimals: dec}),
    max: formatLocalizedDecimal(nb, {locale: decimalLocale, decimals: dec}),
  }
}

// Local string‑based copies of number inputs so the NumberSpinner component
// can display its value even before the store is updated.
const localRanges = reactive({})

// show-more/collapse state by category (false = show only first 10)
const expandedMore = reactive({})

/** true if option at `idx` should be hidden while collapsed */
function shouldHideOption(key, idx) {
  return !expandedMore[key] && idx >= SHOW_MORE_OPTIONS_THRESHOLD
}

/** toggle the "show more" for a category */
function toggleMore(key) {
  expandedMore[key] = !expandedMore[key]
}

function moreRemaining(category) {
  const total = (availableOptionsByKey.value?.[category.key]?.length || 0)
  return Math.max(0, total - SHOW_MORE_OPTIONS_THRESHOLD)
}

// Scroll helper used after filters apply on desktop widths for better UX.
function scrollListTopIfWide() {
  if (window.innerWidth <= 1024) return
  const el = document.getElementById('product-list')

  if (!el) return
  const top = el.getBoundingClientRect().top + window.pageYOffset - 100

  window.scrollTo({top, behavior: 'smooth'})
}

/**
 * Watcher #1 — Mirror store → inputs.
 * When any selection changes, keep localRanges (NumberSpinner v-model) in sync
 * so the spinners always show the current [min,max].
 */
watch(
  () => filterStore.selected,
  () => {
    categoriesWithMeta.value.forEach(cat => {
      if (!cat?.meta || !['PRICE', 'NUMBER'].includes(cat.meta.type)) return;
      const sel = Array.isArray(filterStore.selected[cat.key])
        ? filterStore.selected[cat.key]
        : [cat.meta.min, cat.meta.max];

      localRanges[cat.key] = {
        min: formatLocalizedDecimal(sel[0], {locale: decimalLocale, decimals: cat.meta.__decimals}),
        max: formatLocalizedDecimal(sel[1], {locale: decimalLocale, decimals: cat.meta.__decimals}),
      };
    });
  },
  {immediate: true, deep: true}
);

/**
 * Watcher #2 — Seed/refresh input formatting when meta changes.
 * When categories/meta arrive or precision changes, (re)seed localRanges once
 * (keeps decimals aligned; avoids redundant rewrites).
 */
watch(
  categoriesWithMeta,
  (cats) => {
    cats.forEach(cat => {
      if (!cat?.meta || !['PRICE', 'NUMBER'].includes(cat.meta.type)) return
      const sel = Array.isArray(filterStore.selected[cat.key])
        ? filterStore.selected[cat.key]
        : [cat.meta.min, cat.meta.max]

      // only (re)seed if missing or decimals changed
      if (!localRanges[cat.key] ||
        localRanges[cat.key].__decimals !== cat.meta.__decimals) {
        localRanges[cat.key] = {
          min: formatLocalizedDecimal(sel[0], {locale: decimalLocale, decimals: cat.meta.__decimals}),
          max: formatLocalizedDecimal(sel[1], {locale: decimalLocale, decimals: cat.meta.__decimals}),
          __decimals: cat.meta.__decimals, // helper to avoid unnecessary re-writes
        }
      }
    })
  },
  {immediate: true, deep: true}
)

/**
 * Watcher #3 — Debounced apply.
 * Detect which filter changed, update “last interacted” + keep only its panel
 * open (no fetch), then debounce applyFilters to avoid API spam.
 */
watch(
  () => JSON.stringify(filterStore.selected),
  (newStr, oldStr) => {
    // Guard: ignore during hydration, silenced mutations, apply in flight, or while any filter options are loading (lazy load → inputs mount → emit).
    if (filterStore._isHydrating || filterStore._isResetting || filterStore.silenceSelectedMutations || filterStore.loadingApply || Object.values(filterStore.loadingOptions || {}).some(Boolean)) return
    // compute which category changed last
    let changedKey = null
    try {
      const next = JSON.parse(newStr || '{}')
      const prev = JSON.parse(oldStr || '{}')
      const keys = Array.from(new Set([...Object.keys(next), ...Object.keys(prev)]))
      for (const k of keys) {
        if (JSON.stringify(next[k]) !== JSON.stringify(prev[k])) {
          changedKey = k
        }
      }
      // eslint-disable-next-line no-empty
    } catch {
    }

    if (changedKey) {
      setLastOpenKey(changedKey)
      filterStore.setLastInteractedKey(changedKey)
      // keep UI only, no fetch
      openOnly(changedKey, {fetch: false})
    }

    productStore.setLoaded(false)

    if (debounceTimer) clearTimeout(debounceTimer)

    const delay = debounceMsForBp(getBreakpoint())

    debounceTimer = window.setTimeout(() => {
      filterStore.setLoaded(false)
      // scroll to list top on desktop widths
      scrollListTopIfWide()
      filterStore.applyFilters({refreshOptions: true})
    }, delay)
  }
)

onBeforeUnmount(() => {
  if (debounceTimer) clearTimeout(debounceTimer)
})

/**
 * Commit a changed min/max pair back to the store. Ensures values are valid
 * numbers and clamped within the allowed range before updating the Pinia
 * store, which in turn updates the slider.
 */
function commitRange(key, which) {
  const meta = filterStore.metaFor(key)
  if (!meta) return
  const dec = Math.max(decimalPlaces(meta.min), decimalPlaces(meta.max))
  const norm = {...meta, __decimals: dec, __step: Number(Math.max(1, Number(meta.max) * 0.1).toFixed(dec))}

  const raw = localRanges[key]
  const pMin = parseLocalizedDecimal(raw.min, {locale: decimalLocale})
  const pMax = parseLocalizedDecimal(raw.max, {locale: decimalLocale})

  let [a, b] = filterStore.selected[key]
  const clamp = (v) => Math.min(Math.max(v, norm.min), norm.max)
  if (which === 'min' && Number.isFinite(pMin)) a = clamp(pMin)
  if (which === 'max' && Number.isFinite(pMax)) b = clamp(pMax)
  if (a > b) {
    if (which === 'min') a = Math.min(a, b); else b = Math.max(b, a)
  }

  filterStore.updateRange(key, 0, a)
  filterStore.updateRange(key, 1, b)

  localRanges[key].min = formatLocalizedDecimal(a, {locale: decimalLocale, decimals: dec})
  localRanges[key].max = formatLocalizedDecimal(b, {locale: decimalLocale, decimals: dec})
}
</script>

<template>
  <div
    v-for="category in categoriesWithMeta"
    :key="category.key"
    class="filter-category form prevent-default-checkbox-js">
    <span
      class="heading delta filter-category__title icon-angle-down-after"
      :class="{ '-open': expanded[category.key] }"
      role="button"
      tabindex="0"
      @click="toggleExpand(category.key)"
      @keydown.enter.prevent="toggleExpand(category.key)"
      @keydown.space.prevent="toggleExpand(category.key)">
      {{ filterStore.labelFor(category.key) }}
    </span>
    <div v-show="expanded[category.key]" class="filter-category__body">
      <div class="reset-category-filters-btn" v-if="activeByKey[category.key]"
           @click="filterStore.resetCategory(category.key)">
        <span class="icon-return-before"></span>
        <span class="heading">{{ $t('filterLabels.reset') || 'zurücksetzen' }}</span>
      </div>
      <div v-if="!category.meta || filterStore.loadingOptions[category.key]" class="filter-loading"></div>
      <template v-else>
        <ul v-if="['TPG_OPTION','OPTION'].includes(category.meta.type)"
            class="filter-category__options"
            :class="{ '-with-more-button': ['TPG_OPTION','OPTION'].includes(category.meta.type) && (availableOptionsByKey[category.key]?.length || 0) > SHOW_MORE_OPTIONS_THRESHOLD }">
          <li
            v-for="(opt, idx) in availableOptionsByKey[category.key]"
            :key="opt.tpgCode || opt"
            class="filter-option"
            :class="{ '-hide': shouldHideOption(category.key, idx) }">
            <div class="form-group">
              <div class="checkbox" :class="{ '-checked': isChecked(category.key, valueFor(category.key, opt))}">
                <div class="form-check">
                  <label
                    class="add-on form-check-label"
                    :for="`filter-${category.key}-${valueFor(category.key, opt)}`">
                    <input
                      class="add-on"
                      type="checkbox"
                      :id="`filter-${category.key}-${valueFor(category.key, opt)}`"
                      :name="`filter-checkbox-${category.key}-${valueFor(category.key, opt)}`"
                      :value="valueFor(category.key, opt)"
                      v-model="filterStore.selected[category.key]"
                      @keydown.space.prevent="e => e.target.click()"
                      @keydown.enter.prevent="e => e.target.click()"/>
                    <span class="form-check-text">
                    {{ labelFor(opt) }}
                  </span>
                  </label>
                </div>
              </div>
            </div>
          </li>
        </ul>
        <div
          v-if="['TPG_OPTION','OPTION'].includes(category.meta.type) && (availableOptionsByKey[category.key]?.length || 0) > SHOW_MORE_OPTIONS_THRESHOLD"
          class="filter-category__more">
          <button
            type="button"
            class="btn -btn-link"
            @click="toggleMore(category.key)">
            <span v-if="!expandedMore[category.key]">
              {{
                $te('filterLabels.filterOptions.showMoreLabel') ? $t('filterLabels.filterOptions.showMoreLabel', {count: moreRemaining(category)}) : 'Mehr anzeigen'
              }}
            </span>
            <span v-else>
                {{
                $te('filterLabels.filterOptions.showLessLabel') ? $t('filterLabels.filterOptions.showLessLabel') : 'Weniger anzeigen'
              }}
            </span>
          </button>
        </div>

        <label v-else-if="category.meta.type === 'BOOLEAN'" class="switch">
          <input
            type="checkbox"
            v-model="filterStore.selected[category.key]"
            :id="`filter-toggle-${category.key}`"
            :name="`filter-toggle-${category.key}`"
            @keydown.space.prevent="e => e.target.click()"
            @keydown.enter.prevent="e => e.target.click()"/>
          <span class="slider"></span>
          <span class="switch-label">{{ $t('filterLabels.yes') }}</span>
        </label>

        <div
          v-else-if="['PRICE','NUMBER'].includes(category.meta.type)"
          class="filter-range">
          <vue-slider
            v-if="filterStore.selected[category.key]?.length === 2"
            v-model="filterStore.selected[category.key]"
            :min="category.meta.min"
            :max="category.meta.max"
            :interval="category.meta.__interval"
            tooltip="none"
            v-no-a11y
            @change="val => { filterStore.markRangeTouched(category.key); snapSelectionToGrid(category.key, val) }"
            @drag-end="(val, idx) => { filterStore.markRangeTouched(category.key); snapSelectionToGrid(category.key, val, idx) }"
          />
          <div v-if="localRanges[category.key]" class="filter-range__inputs">
            <label>
            <span class="bold">
              {{ $t('filterLabels.min') }} {{ getUnitOrCurrency(category.meta) }}
            </span>
              <NumberSpinner
                :id="`filter-price-min-${category.key}`"
                :name="`price_min_${category.key}`"
                v-model="localRanges[category.key].min"
                :min="category.meta.min"
                :max="category.meta.max"
                :step="category.meta.__step"
                :decimals="category.meta.__decimals"
                :locale="decimalLocale"
                role="min"
                :peer="parseLocalizedDecimal(localRanges[category.key]?.max ?? String(category.meta.max), { locale: decimalLocale })"
                @update:modelValue="() => commitRange(category.key, 'min')"/>
            </label>
            <label>
            <span class="bold">
              {{ $t('filterLabels.max') }} {{ getUnitOrCurrency(category.meta) }}
            </span>
              <NumberSpinner
                :id="`filter-price-max-${category.key}`"
                :name="`price_max_${category.key}`"
                v-model="localRanges[category.key].max"
                :min="category.meta.min"
                :max="category.meta.max"
                :step="category.meta.__step"
                :decimals="category.meta.__decimals"
                :locale="decimalLocale"
                role="max"
                :peer="parseLocalizedDecimal(localRanges[category.key]?.min ?? String(category.meta.min), { locale: decimalLocale })"
                @update:modelValue="() => commitRange(category.key, 'max')"/>
            </label>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
